import { ArrowRight, Check, X } from 'lucide-react';
import { Link } from 'react-router';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { LocalizedAnchor } from '@/components/LocalizedLink';
import { buttonVariants } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { JsonLd } from '@/components/JsonLd';
import { useT } from '@/i18n/provider';
import {
  comparisonRows,
  migrationCards,
  highlightCards,
  duos,
  COMPETITORS,
  heroIcon as HeroIcon,
  ctaIcon as CtaIcon,
  migrateIcon as MigrateIcon,
  type CellValue,
} from '@/data/compare-data';

/**
 * Resolve a cell value through i18n. Values prefixed with `t:` are looked up
 * under `compare.cell.<key>`. Booleans render as Included/No icons. Plain
 * strings (brand names, prices) are returned verbatim.
 */
function useResolveCell() {
  const t = useT();
  return (value: CellValue): React.ReactNode => {
    if (value === true) {
      return (
        <span className="inline-flex items-center gap-1 text-emerald-400 font-medium">
          <Check className="h-4 w-4" /> {t('compare.cell.included')}
        </span>
      );
    }
    if (value === false) {
      return (
        <span className="inline-flex items-center gap-1 text-red-400">
          <X className="h-4 w-4" /> {t('compare.cell.no')}
        </span>
      );
    }
    if (typeof value === 'string' && value.startsWith('t:')) {
      return <span>{t(`compare.cell.${value.slice(2)}`)}</span>;
    }
    return <span>{value}</span>;
  };
}

function HighlightCard({ icon: Icon, id }: { icon: React.ElementType; id: string }) {
  const t = useT();
  return (
    <div className="rounded-2xl bg-gray-900/50 border border-gray-800 p-8 hover:border-primary-500/30 transition-colors">
      <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mb-5">
        <Icon className="h-6 w-6 text-primary-400" />
      </div>
      <h3 className="text-xl font-bold text-white mb-3">{t(`compare.highlight.${id}.title`)}</h3>
      <p className="text-gray-400 leading-relaxed">{t(`compare.highlight.${id}.description`)}</p>
    </div>
  );
}

function MigrationCard({ step, platformKey, stepKeys }: {
  step: number;
  platformKey: string;
  stepKeys: string[];
}) {
  const t = useT();
  const platform = t(`compare.migrate.${platformKey}.title`);
  const cardLabel = t('compare.migrate.cardLabel').replace('{platform}', platform);
  return (
    <div className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 hover:border-primary-500/30 transition-colors">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-400 text-sm font-bold flex-shrink-0">
          {step}
        </span>
        <h3 className="text-white font-semibold">{cardLabel}</h3>
      </div>
      <ol className="space-y-3">
        {stepKeys.map((key, i) => (
          <li key={key} className="flex items-start gap-2.5 text-sm text-gray-400">
            <span className="text-primary-400 font-medium mt-px">{i + 1}.</span>
            <span>{t(`compare.migrate.${platformKey}.${key}`)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function DuoLinkCard({ slug, competitors }: { slug: string; competitors: readonly ('splunk' | 'datadog' | 'elk')[] }) {
  const t = useT();
  const competitorNames = competitors.map((c) => COMPETITORS[c].name);
  const heading = competitorNames.join(' vs ');
  return (
    <Link
      to={`/compare/${slug}`}
      className="group rounded-2xl bg-gray-900/50 border border-gray-800 p-6 hover:border-primary-500/40 hover:bg-gray-900/70 transition-all"
    >
      <div className="text-xs uppercase tracking-wider text-primary-400 font-semibold mb-2">
        LogPulse vs {heading}
      </div>
      <div className="text-lg font-semibold text-white mb-3 leading-tight">
        {t(`compare.duo.${slug}.heading`)}
      </div>
      <div className="inline-flex items-center gap-1 text-sm text-primary-400 font-medium group-hover:gap-2 transition-all">
        {t('compare.duo.cta')} <ArrowRight className="h-4 w-4" />
      </div>
    </Link>
  );
}

export function ComparisonPage() {
  const t = useT();
  const resolveCell = useResolveCell();

  useDocumentMeta({
    title: t('compare.hubMeta.title'),
    description: t('compare.hubMeta.description'),
  });

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: t('compare.faq.q1').replace('{a}', 'Splunk').replace('{b}', 'Datadog'),
        acceptedAnswer: { '@type': 'Answer', text: t('compare.faq.a1').replace('{a}', 'Splunk').replace('{b}', 'Datadog') },
      },
      {
        '@type': 'Question',
        name: t('compare.faq.q2').replace('{a}', 'Splunk').replace('{b}', 'Datadog'),
        acceptedAnswer: { '@type': 'Answer', text: t('compare.faq.a2').replace('{a}', 'Splunk').replace('{b}', 'Datadog') },
      },
      {
        '@type': 'Question',
        name: t('compare.faq.q4'),
        acceptedAnswer: { '@type': 'Answer', text: t('compare.faq.a4') },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <JsonLd data={faqJsonLd} />
      <LandingHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-6">
            <HeroIcon className="h-4 w-4" />
            {t('compare.hubBadge')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('compare.hubTitle')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            {t('compare.hubSubtitle')}
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-900/80 border-b border-gray-800">
                    <th className="text-left p-4 text-gray-400 font-semibold min-w-[180px]">
                      {t('compare.tableHeader.feature')}
                    </th>
                    <th className="text-left p-4 min-w-[150px]">
                      <span className="text-primary-400 font-bold">LogPulse</span>
                    </th>
                    <th className="text-left p-4 text-gray-300 font-semibold min-w-[150px]">Splunk</th>
                    <th className="text-left p-4 text-gray-300 font-semibold min-w-[150px]">Datadog</th>
                    <th className="text-left p-4 text-gray-300 font-semibold min-w-[150px]">ELK Stack</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row.featureKey} className="border-b border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                      <td className="p-4 text-white font-medium">{t(`compare.rows.${row.featureKey}`)}</td>
                      <td className="p-4 text-primary-300 font-medium bg-primary-500/5">
                        {resolveCell(row.logpulse)}
                      </td>
                      <td className="p-4 text-gray-400">{resolveCell(row.splunk)}</td>
                      <td className="p-4 text-gray-400">{resolveCell(row.datadog)}</td>
                      <td className="p-4 text-gray-400">{resolveCell(row.elk)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Direct duo comparisons */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
              {t('compare.duosBadge')}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('compare.duosTitle')}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t('compare.duosSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {duos.map((duo) => (
              <DuoLinkCard key={duo.slug} slug={duo.slug} competitors={duo.competitors} />
            ))}
          </div>
        </div>
      </section>

      {/* Built for Modern Teams */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('compare.highlight.title')}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t('compare.highlight.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {highlightCards.map((h) => (
              <HighlightCard key={h.id} icon={h.icon} id={h.id} />
            ))}
          </div>
        </div>
      </section>

      {/* Migration Section */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
              <MigrateIcon className="h-4 w-4" />
              {t('compare.migrate.badge')}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('compare.migrate.title')}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t('compare.migrate.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {migrationCards.map((card, idx) => (
              <MigrationCard
                key={card.platformKey}
                step={idx + 1}
                platformKey={card.platformKey}
                stepKeys={card.stepKeys}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto rounded-2xl bg-gradient-to-b from-primary-500/10 to-gray-900/50 border border-primary-500/20 p-8 sm:p-12">
            <CtaIcon className="h-10 w-10 text-primary-400 mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('compare.cta.title')}
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed max-w-xl mx-auto">
              {t('compare.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.logpulse.io/sign-in"
                className={buttonVariants({ variant: 'gradient', size: 'lg' })}
              >
                {t('compare.cta.primary')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
              <LocalizedAnchor href="/docs" className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                {t('compare.cta.secondary')}
              </LocalizedAnchor>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
